import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { GraduationCap, ShieldCheck, UserCheck, Search, RefreshCw, Trash2, ExternalLink, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../components/AuthGuard';
import { Link } from 'react-router-dom';

export default function PortalAccessManager() {
    const { user } = useAuth();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);

    // Form fields
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [programName, setProgramName] = useState('Full Stack Software Engineering');
    const [cohort, setCohort] = useState(`Class of ${new Date().getFullYear()}`);

    const fetchPortalStudents = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'system_roles'));
            const list = [];
            snap.forEach(d => {
                const data = d.data();
                if (data.role === 'student' && data.payment_status === 'paid_school_fees') {
                    list.push({ id: d.id, ...data });
                }
            });
            list.sort((a, b) => new Date(b.updatedAt || b.addedAt || 0) - new Date(a.updatedAt || a.addedAt || 0));
            setStudents(list);
        } catch (err) {
            console.error('Error fetching portal students:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPortalStudents();
    }, []);

    const handleGrantPortalAccess = async (e) => {
        e.preventDefault();
        const cleanEmail = email.trim().toLowerCase();
        const cleanName = fullName.trim();

        if (!cleanEmail || !cleanName) {
            alert('Please provide student email and full name.');
            return;
        }

        setSubmitting(true);
        setStatusMessage(null);

        try {
            const timestamp = new Date().toISOString();

            // 1. Authorize in system_roles with full access credentials
            await setDoc(doc(db, 'system_roles', cleanEmail), {
                role: 'student',
                displayName: cleanName,
                status: 'active',
                gate: 2,
                payment_status: 'paid_school_fees',
                updatedAt: timestamp,
                assignedVia: 'NexusPortalAccessManager',
                assignedBy: user?.email || 'admin'
            }, { merge: true });

            // 2. Update Student Registry
            await setDoc(doc(db, 'students', cleanEmail), {
                name: cleanName,
                fullName: cleanName,
                email: cleanEmail,
                status: 'Active',
                fee_status: 'PAID',
                gate: 2,
                programName: programName.trim(),
                cohort: cohort.trim(),
                schoolFeesPaidAt: timestamp,
                updatedAt: timestamp
            }, { merge: true });

            setStatusMessage({
                type: 'success',
                text: `Site B Student Portal Access granted for ${cleanName} (${cleanEmail}). The student can now log in at portal.dynamicindutechno.solutions.`
            });

            setEmail('');
            setFullName('');
            fetchPortalStudents();

        } catch (err) {
            console.error('Error granting portal access:', err);
            setStatusMessage({
                type: 'error',
                text: 'Failed to grant portal access: ' + err.message
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRevokePortalAccess = async (studentEmail) => {
        if (!window.confirm(`Revoke Site B Portal Access for ${studentEmail}? (Downgrades to onboarding only)`)) return;
        try {
            await setDoc(doc(db, 'system_roles', studentEmail), {
                payment_status: 'paid_onboarding',
                gate: 1,
                updatedAt: new Date().toISOString()
            }, { merge: true });
            await setDoc(doc(db, 'students', studentEmail), {
                status: 'Paid 1020',
                gate: 1
            }, { merge: true });
            fetchPortalStudents();
        } catch (e) {
            alert('Failed to revoke portal access: ' + e.message);
        }
    };

    const filtered = students.filter(s => {
        const query = searchQuery.toLowerCase();
        return (
            (s.id && s.id.toLowerCase().includes(query)) ||
            (s.displayName && s.displayName.toLowerCase().includes(query)) ||
            (s.email && s.email.toLowerCase().includes(query))
        );
    });

    return (
        <div className="space-y-8">
            {/* Navigation Tabs */}
            <div className="flex items-center gap-3 border-b border-zinc-800 pb-4">
                <Link
                    to="/onboarding-access"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-xs uppercase tracking-wider transition-colors"
                >
                    <ExternalLink size={16} />
                    Onboarding Form Access (Gate 1)
                </Link>
                <Link
                    to="/portal-access"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#8B1A2D] text-white font-bold text-xs uppercase tracking-wider"
                >
                    <ShieldCheck size={16} />
                    Site B Student Portal Access (Gate 2)
                </Link>
            </div>

            {/* Header */}
            <div>
                <h1 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                    <GraduationCap className="text-[#C9973A]" size={28} />
                    Site B Student Portal Access Provisioning
                </h1>
                <p className="text-zinc-400 text-sm mt-1">
                    Directly authorize student accounts to access the core learning portal (<span className="text-[#C9973A] font-mono">portal.dynamicindutechno.solutions</span>), bypass the tuition gate, and activate live course access.
                </p>
            </div>

            {/* Status Alert */}
            {statusMessage && (
                <div className={`p-4 rounded-xl border flex items-center gap-3 ${
                    statusMessage.type === 'success'
                        ? 'bg-emerald-950/40 border-emerald-800 text-emerald-200'
                        : 'bg-red-950/40 border-red-800 text-red-200'
                }`}>
                    <CheckCircle2 size={20} className={statusMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'} />
                    <span className="text-sm font-semibold">{statusMessage.text}</span>
                </div>
            )}

            {/* Quick Add Form Card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-xl">
                <div className="text-xs font-black uppercase tracking-widest text-[#C9973A] mb-4">
                    Fast Portal Provisioning Console
                </div>
                <form onSubmit={handleGrantPortalAccess} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                                Student Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="e.g. student@gmail.com"
                                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8B1A2D] transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                                Full Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                required
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder="e.g. Jane Doe"
                                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8B1A2D] transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                                Academic Program
                            </label>
                            <input
                                type="text"
                                value={programName}
                                onChange={(e) => setProgramName(e.target.value)}
                                placeholder="e.g. Software Engineering"
                                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8B1A2D] transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                                Assigned Cohort
                            </label>
                            <input
                                type="text"
                                value={cohort}
                                onChange={(e) => setCohort(e.target.value)}
                                className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-[#8B1A2D] transition-colors"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-end pt-2 border-t border-zinc-800/80">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-6 py-2.5 bg-[#8B1A2D] hover:bg-[#A32036] text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-[#8B1A2D]/20 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <RefreshCw className="animate-spin" size={16} />
                            ) : (
                                <UserCheck size={16} />
                            )}
                            <span>{submitting ? 'Activating Portal...' : 'Grant Site B Portal Access'}</span>
                        </button>
                    </div>
                </form>
            </div>

            {/* Filter and Live List */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-xl space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-base font-bold text-white uppercase tracking-wider">
                            Active Site B Portal Students ({students.length})
                        </h2>
                        <p className="text-zinc-500 text-xs">Students cleared for full access to portal.dynamicindutechno.solutions</p>
                    </div>

                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-2.5 text-zinc-500" size={16} />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Filter by name or email..."
                            className="w-full bg-zinc-800 border border-zinc-700 text-white text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-[#8B1A2D]"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="py-12 text-center text-zinc-500 text-sm">
                        Loading active portal students...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-12 text-center text-zinc-500 text-sm">
                        No active portal students found matching your filter.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-zinc-800/60 text-zinc-400 uppercase font-black tracking-wider border-b border-zinc-800">
                                <tr>
                                    <th className="py-3 px-4">Student Name</th>
                                    <th className="py-3 px-4">Email</th>
                                    <th className="py-3 px-4">Portal Status</th>
                                    <th className="py-3 px-4">Gate Level</th>
                                    <th className="py-3 px-4">Last Updated</th>
                                    <th className="py-3 px-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-800 text-zinc-300">
                                {filtered.map((s) => (
                                    <tr key={s.id} className="hover:bg-zinc-800/30 transition-colors">
                                        <td className="py-3 px-4 font-bold text-white">
                                            {s.displayName || 'Student'}
                                        </td>
                                        <td className="py-3 px-4 font-mono text-zinc-400">
                                            {s.id}
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-950 text-emerald-400 border border-emerald-800">
                                                Active (Full Portal Access)
                                            </span>
                                        </td>
                                        <td className="py-3 px-4">
                                            <span className="text-[#C9973A] font-bold">Gate 2 (Tuition Cleared)</span>
                                        </td>
                                        <td className="py-3 px-4 text-zinc-500">
                                            {s.updatedAt ? new Date(s.updatedAt).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <button
                                                type="button"
                                                onClick={() => handleRevokePortalAccess(s.id)}
                                                className="px-2.5 py-1 rounded bg-zinc-800 hover:bg-red-950 text-zinc-400 hover:text-red-400 text-[11px] font-bold flex items-center gap-1 transition-colors ml-auto"
                                                title="Revoke portal access (downgrades to onboarding)"
                                            >
                                                <Trash2 size={12} />
                                                <span>Revoke Portal</span>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
